import type { FileImportSummaryResponse } from 'api/__GENERATED';
import * as R from 'remeda';
import { errorWrapper } from 'utility/error';

import { apiClient } from './api';
import { toFileDomain } from './transformers/toFileDomain';

// Fetches a paginated page of integration files and shapes them onto the
// Data.File domain. Pagination metadata lives in the `x-pagination` response
// header, not in the body — errorWrapper's onSuccess gives us the AxiosResponse
// so we can read the header alongside the typed body.
async function getFiles(args: {
  tenantId: string;
  pageNumber: number;
  pageSize: number;
}) {
  const { tenantId, pageNumber, pageSize } = args;

  const apiCall = apiClient.File_GetFiles({
    params: { tenantId },
    queries: { PageNumber: pageNumber, PageSize: pageSize },
  });

  return await errorWrapper({
    apiCall,
    error: {
      message: '',
      severity: 'warning',
      title: 'Failed to load files',
    },
    onSuccess: (data, response) => {
      const dtos: FileImportSummaryResponse = data ?? [];
      const { fileDomain } = toFileDomain({ dtos });

      const pagination = parsePaginationHeader(
        response?.headers['x-pagination'],
      );

      return { fileDomain, pagination };
    },
  });
}

// Legacy contract: pagination is shipped as a JSON-encoded string in the
// `x-pagination` response header. Mirrors api_old behaviour. Falls back to
// a single-page result if the header is missing or unparseable.
function parsePaginationHeader(raw: unknown): System.Pagination {
  if (typeof raw !== 'string') return { page: 1, totalPages: 1 };

  try {
    const parsed = JSON.parse(raw) as {
      CurrentPage?: number;
      TotalPages?: number;
    };
    if (!R.isDefined(parsed.CurrentPage)) return { page: 1, totalPages: 1 };
    return { page: parsed.CurrentPage, totalPages: parsed.TotalPages };
  } catch {
    return { page: 1, totalPages: 1 };
  }
}

export { getFiles };
